/**
 * Точка входа кабинета.
 *
 * Маршрутизация браузерная: раздача статики обязана отдавать index.html на
 * любой неизвестный путь, иначе прямая ссылка на раздел вернёт 404 от CDN.
 * Требование записано в README.
 *
 * Дерево устроено так, что до отрисовки известно, кто вошёл: `SessionProvider`
 * стоит выше маршрутов, а сами маршруты делятся на три группы — публичные
 * (ссылки из писем), вход и оболочка. Экран не решает вопрос доступа сам,
 * иначе рано или поздно найдётся путь, который забыли закрыть.
 */

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';

import './styles/app.css';

import { Login } from './routes/auth/Login';
import { Forgot } from './routes/auth/Forgot';
import { Reset } from './routes/auth/Reset';
import { Verify } from './routes/auth/Verify';
import { Invite } from './routes/auth/Invite';
import { Signup } from './routes/auth/Signup';
import { Audit } from './routes/Audit';
import { AuditClaim } from './routes/AuditClaim';
import { AuditReportPage } from './routes/AuditReportPage';
import { Unsubscribe } from './routes/Unsubscribe';
import { SharedReportPage } from './routes/SharedReportPage';
import { OwnershipAccept } from './routes/OwnershipAccept';
import { NotBuiltYet, Shell } from './routes/Shell';
import { SECTIONS } from './sections';
import { Overview } from './routes/Overview';
import { Plan } from './routes/Plan';
import { Checkout } from './routes/Checkout';
import { PaymentReturn } from './routes/PaymentReturn';
import { Members } from './routes/Members';
import { Clients } from './routes/Clients';
import { Settings } from './routes/Settings';
import { Engines } from './routes/Engines';
import { Visibility } from './routes/Visibility';
import { History } from './routes/History';
import { Recommendations } from './routes/Recommendations';
import { Alerts } from './routes/Alerts';
import { Reports } from './routes/Reports';
import { BrandList } from './routes/brands/BrandList';
import { BrandCard } from './routes/brands/BrandCard';
import { CompetitorsScreen } from './routes/brands/CompetitorsScreen';
import { Onboarding } from './routes/brands/Onboarding';
import { Questions } from './routes/questions/Questions';
import { Runs } from './routes/runs/Runs';
import { Empty, ErrorNotice, Skeleton } from './ui/state';
import { SessionProvider, useSessionState } from './session';
import { safePath } from './return-to';
import { pendingClaim } from './pending-claim';
import { StepUpProvider } from './ui/StepUp';
import { I18nProvider, useAccountLocale } from './i18n/react';
import type { Session } from './api/auth';

/**
 * Ссылка старого письма с токеном в пути → канонический адрес с `?token=`.
 *
 * `replace`, чтобы «назад» не возвращало на мёртвый адрес.
 */
function LegacyTokenLink({ to }: { to: string }) {
  const { token = '' } = useParams();
  return <Navigate to={`${to}?token=${encodeURIComponent(token)}`} replace />;
}

/**
 * Короткий адрес отчёта аудита: `/a/<слаг>` → `/audit/<слаг>`.
 *
 * По такой ссылке отчёт смотрит владелец, и она мёртвая: маршрута `/a/*` нет,
 * гость по ней попадает в гостевую ветку `*` и уезжает на форму входа. То есть
 * человек, которому переслали отчёт, видит вместо чисел просьбу войти — ровно
 * тот дефект, ради которого публичный маршрут и заводился.
 *
 * Куда ведёт продукт сегодня, видно из самого API: `report_url` он строит как
 * `{APP}/audit/{slug}`. Поэтому короткий адрес не дублирует экран, а
 * переадресует на канонический, `replace` — чтобы «назад» не возвращало на
 * мёртвый адрес.
 */
function ShortAuditLink() {
  const { slug = '' } = useParams();
  return <Navigate to={`/audit/${encodeURIComponent(slug)}`} replace />;
}

/** Ссылка старого письма о передаче владения → раздел приёма передачи. */
function LegacyOwnershipLink() {
  const { transferId = '' } = useParams();
  return (
    <Navigate to={`/ownership-transfers/${encodeURIComponent(transferId)}`} replace />
  );
}

function NotFound() {
  return (
    <Empty
      eyebrow="Адрес не открылся"
      title="Такого раздела в кабинете нет"
      action={
        <a className="btn btn-ghost" href="/">
          Вернуться к обзору бренда
        </a>
      }
    >
      Возможно, адрес набран с ошибкой или раздел переехал. Отсюда можно вернуться на
      рабочий экран.
    </Empty>
  );
}

function App() {
  const { state, adopt, reload } = useSessionState();
  const location = useLocation();
  const navigate = useNavigate();

  // Язык кабинета — настройка учётной записи (`Account.locale` в контракте), и
  // она приезжает внутри сессии. До сессии работает последний выбор из
  // хранилища браузера: экран входа тоже кто-то читает.
  useAccountLocale(state.kind === 'signed' ? state.session.user.locale : null);

  /**
   * Общая посадка после любого успешного входа — по паролю, по Яндекс ID, по
   * ссылке подтверждения, по приглашению. Сессия уже пришла ответом, второй
   * запрос за ней не нужен.
   *
   * Возвращаем туда, куда человек шёл до того, как его отправили на вход. Путь
   * берётся только из состояния навигации и только относительный: чужой адрес
   * в этом месте — открытый редирект сразу после успешного входа.
   */
  const land = (session: Session) => {
    const from = (location.state as { from?: string } | null)?.from;
    adopt(session);
    // Состояние маршрута переживает не всякий вход: возврат от Яндекс ID —
    // новая навигация, а ссылку подтверждения почты открывают из письма и
    // обычно в другой вкладке. Если человек шёл забирать отчёт бесплатного
    // аудита, намерение лежит отдельно и переживает оба разрыва.
    const claim = pendingClaim();
    const fallback = claim ? `/audits/claim?slug=${encodeURIComponent(claim)}` : '/';
    navigate(from ? safePath(from) : fallback, { replace: true });
  };

  if (state.kind === 'checking') {
    return (
      <main className="screen">
        <Skeleton rows={2} />
      </main>
    );
  }

  if (state.kind === 'failed') {
    return (
      <main className="screen">
        <ErrorNotice
          error={state.error}
          action={
            <button className="btn btn-ghost btn-sm" type="button" onClick={reload}>
              Повторить
            </button>
          }
        />
      </main>
    );
  }

  const signedIn = state.kind === 'signed';

  return (
    <Routes>
      {/* Пути из писем. Они работают одинаково для вошедшего и для гостя. */}
      <Route path="/auth/verify" element={<Verify onDone={land} />} />
      <Route path="/auth/reset" element={<Reset onDone={land} />} />
      <Route
        path="/invite/:token"
        element={<Invite signedIn={signedIn} onDone={land} />}
      />

      {/* Адреса, которые уже разосланы письмами прежней сборки.
          API строил `/verify/<token>`, `/reset/<token>`, `/ownership/<id>` и
          `/settings/sessions` — маршрутов под них не было ни одного, и гость
          падал в guest-ветку `path="*"`, то есть молча оказывался на форме
          входа. Регистрацию нельзя было завершить, пароль нельзя было
          восстановить. Ссылки писем теперь строятся правильно, но письма живут
          трое суток, а ссылка приглашения — семь; эти переходы держат уже
          отправленное. Удалять их можно не раньше, чем истечёт последнее. */}
      <Route path="/verify/:token" element={<LegacyTokenLink to="/auth/verify" />} />
      <Route path="/reset/:token" element={<LegacyTokenLink to="/auth/reset" />} />
      <Route path="/ownership/:transferId" element={<LegacyOwnershipLink />} />
      <Route path="/settings/sessions" element={<Navigate to="/settings" replace />} />

      {/* Бесплатный аудит — вход в воронку (спека §4), и он публичный: сюда
          приходят с витрины по ссылке `?domain=…`, ещё не будучи никем. Стоит
          рядом с приглашением по той же причине — маршрут, доступный без
          сессии. */}
      <Route path="/audit" element={<Audit />} />

      {/* Публичный отчёт бесплатного аудита. Адрес выдаёт сам сервис полем
          `report_url`, экран результата обещает «Отчёт останется доступным по
          этой ссылке ещё семь дней» — а маршрута не было, и гость по
          собственному адресу продукта уезжал на форму входа. */}
      <Route path="/audit/:slug" element={<AuditReportPage />} />

      {/* Отказ от писем о продукте. Публичный по той же причине, по которой
          публична сама ссылка в письме: человек, просящий перестать ему писать,
          не обязан вспоминать пароль. */}
      <Route path="/unsubscribe/:token" element={<Unsubscribe />} />

      {/* Короткая ссылка на тот же отчёт. Стоит рядом с маршрутами писем по той
          же причине: адрес уже разослан, и держать его обязан продукт, а не
          память того, кто им поделился. */}
      <Route path="/a/:slug" element={<ShortAuditLink />} />

      {/* Публичная ссылка отчёта: `share_url` вида `{base}/r/{slug}`. Кабинет
          рядом с кнопкой обещает «Кто угодно с этой ссылкой увидит числа за
          период», а маршрута не было вовсе: гость улетал на форму входа,
          вошедший получал «Ошибка 404». */}
      <Route path="/r/:slug" element={<SharedReportPage />} />

      {signedIn ? (
        <>
          {/* Вошедшему форма входа не нужна: уводим туда, куда он шёл.
              Здесь стояло `to="/"`, и этот редирект монтируется в тот же
              коммит, в котором `land()` вызывает `navigate`, — то есть
              перекрывал его. Из-за этого ломались все ссылки, ведущие внутрь
              оболочки, включая `/billing/return`: у клиента с истёкшей сессией
              результат платежа просто не показывался. */}
          <Route
            path="/auth/*"
            element={
              <Navigate
                to={safePath((location.state as { from?: string } | null)?.from)}
                replace
              />
            }
          />

          <Route element={<Shell session={state.session} />}>
            <Route path="/" element={<Overview />} />
            <Route path="/plan" element={<Plan />} />
            <Route path="/plan/checkout" element={<Checkout />} />
            {/* Адрес возврата из ЮKassa. Он же приходит в письме от банка,
                поэтому лежит отдельно от раздела тарифа и открывается прямой
                ссылкой. */}
            <Route path="/billing/return" element={<PaymentReturn />} />
            {/* `/billing` сам по себе маршрутом не был, и человек, набравший
                его руками или пришедший по старой ссылке, получал «Ошибка
                404 · Такого раздела нет». Раздел есть, он называется «Тариф». */}
            <Route path="/billing" element={<Navigate to="/plan" replace />} />
            {/* Присвоение аудита. В меню его нет — заходят сюда с публичного
                отчёта, а не из навигации, — но оболочка закрывает адрес по
                матрице так же, как раздел: `OFF_MENU` в sections.ts. */}
            <Route path="/audits/claim" element={<AuditClaim />} />
            {/* Приём владения: ссылка из письма преемнику. Тоже вне меню и
                тоже закрыт матрицей — `OFF_MENU` в sections.ts. */}
            <Route
              path="/ownership-transfers/:transferId"
              element={<OwnershipAccept />}
            />
            <Route path="/members" element={<Members />} />
            <Route path="/clients" element={<Clients />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/engines" element={<Engines />} />
            <Route path="/visibility" element={<Visibility />} />
            <Route path="/history" element={<History />} />
            <Route path="/recommendations" element={<Recommendations />} />
            <Route path="/alerts" element={<Alerts />} />
            <Route path="/attention" element={<Alerts attention />} />
            <Route path="/reports" element={<Reports />} />

            {/* Первый вход после регистрации: спека §4 начинает путь клиента с
                разбора сайта, а не с анкеты. Тот же мастер живёт и на
                «/brands/new» — с другим заголовком, потому что первый бренд и
                десятый это разные разговоры. */}
            <Route path="/onboarding" element={<Onboarding first />} />
            <Route path="/brands" element={<BrandList />} />
            <Route path="/brands/new" element={<Onboarding />} />
            <Route path="/brands/:brandId" element={<BrandCard />} />
            <Route path="/competitors" element={<CompetitorsScreen />} />
            <Route path="/questions" element={<Questions />} />
            <Route path="/runs" element={<Runs />} />
            {SECTIONS.filter((section) => !section.ready).map((section) => (
              <Route key={section.path} path={section.path} element={<NotBuiltYet />} />
            ))}
            <Route path="*" element={<NotFound />} />
          </Route>
        </>
      ) : (
        <>
          <Route path="/auth/login" element={<Login onDone={land} />} />
          <Route path="/auth/signup" element={<Signup />} />
          <Route path="/auth/forgot" element={<Forgot />} />
          {/* Куда человек шёл — запоминаем и вернём после входа. */}
          <Route
            path="*"
            element={
              <Navigate
                to="/auth/login"
                replace
                state={{ from: location.pathname + location.search }}
              />
            }
          />
        </>
      )}
    </Routes>
  );
}

const root = document.getElementById('root');
if (!root) throw new Error('Не найден корневой элемент #root');

createRoot(root).render(
  <StrictMode>
    <I18nProvider>
      <BrowserRouter>
        <SessionProvider>
          <StepUpProvider>
            <App />
          </StepUpProvider>
        </SessionProvider>
      </BrowserRouter>
    </I18nProvider>
  </StrictMode>,
);
